//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  CampaignType,
  MediaFilterParamKey,
} from '@apis/content-library/contentLibraryApiModel';
import { useIsRetailerClient } from '@campaigns/hooks/useIsRetailerClient';
import { DefaultButton } from '@components/atoms/button/Button';
import HR from '@components/atoms/hr/HR';
import { BodyText } from '@components/atoms/text/Text';
import { CollapsibleSection } from '@components/media-filters/CollapsibleSection';
import { SelectedOptions } from '@components/media-filters/SelectedOptions';
import {
  FilterOption,
  FilterSection,
  FilterSectionTitle,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import { DEFAULT_SELECTED_PRODUCTS_FILTER_SECTION } from '@components/media-filters/mediaFilterConstants';
import { getSection } from '@components/media-filters/mediaFiltersUtils';
import { useProductDetailsQuery } from '@content-drawer/filters/labels/hooks/useProductsQuery';
import { getDisabledFilters } from '@content-drawer/selectors/getContentDrawerSettings';
import { isDefined } from '@utils/filterPredicates';

const EXCLUDED_PARAM_KEYS: MediaFilterParamKey[] = ['userIds', 'isDistributed'];
const SUBSECTION_PARAM_KEYS: MediaFilterParamKey[] = [
  'labels',
  'productIds',
  'campaignSubTypes',
];

const getDistributionTitle = (isDistributed: boolean | undefined) =>
  isDistributed ? 'Distributed to' : 'Not distributed to';

const handleCampaignTypeDeselection = (
  paramKey: Extract<MediaFilterParamKey, 'campaignTypes'>,
  selected: FilterOption<FilterValue>,
  sections: Map<MediaFilterParamKey, FilterSection<FilterValue>>,
  onChange: (
    sections: Map<MediaFilterParamKey, FilterSection<FilterValue>>,
  ) => void,
) => {
  const section = sections.get(paramKey);

  if (!isDefined(section)) {
    return;
  }

  const updatedSections = new Map<
    MediaFilterParamKey,
    FilterSection<FilterValue>
  >();

  const subsection = sections.get('campaignSubTypes');

  // if deselecting "Sampling" also clear all subtypes
  if (isDefined(subsection) && selected.value === CampaignType.Sampling) {
    updatedSections.set(subsection.paramKey, {
      ...subsection,
      options: subsection.options.map((option) => ({
        ...option,
        isSelected: false,
      })),
    });
  } else if (isDefined(subsection)) {
    updatedSections.set(subsection.paramKey, {
      ...subsection,
      options: subsection.options.map((option) => ({
        ...option,
        isSelected: option.value === selected.value ? false : option.isSelected,
      })),
    });
  }

  updatedSections.set(section.paramKey, {
    ...section,
    options: section.options.map((option) => ({
      ...option,
      isSelected: option.value === selected.value ? false : option.isSelected,
    })),
  });

  onChange(updatedSections);
};

export interface ExtraSelectedSection {
  onDeselect: (value: string) => void;
  options: FilterOption<string>[];
  title: string;
}

export interface SelectedFiltersProps<T extends FilterValue> {
  extraSection?: ExtraSelectedSection;
  sections: Map<MediaFilterParamKey, FilterSection<T>>;
  onChange: (
    sections: Map<MediaFilterParamKey, FilterSection<FilterValue>>,
  ) => void;
  onReset: () => void;
}

export const SelectedFilters = <T extends FilterValue>({
  extraSection,
  onChange,
  onReset,
  sections,
}: SelectedFiltersProps<T>) => {
  const disabledFilters = useSelector(getDisabledFilters);

  const { data: isRetailer } = useIsRetailerClient();

  const extraSectionCount = extraSection?.options.length ?? 0;
  const sectionsArray = Array.from(sections.values());
  const { filteredSections, filtersCount } = sectionsArray.reduce<{
    filtersCount: number;
    filteredSections: FilterSection<T>[];
  }>(
    (data, section) => {
      if (!EXCLUDED_PARAM_KEYS.includes(section.paramKey)) {
        const options = section.options.filter((option) => option.isSelected);
        data.filtersCount += options.length;
        if (options.length > 0) {
          data.filteredSections.push({
            ...section,
            options,
          });
        }
      }
      return data;
    },
    { filtersCount: 0, filteredSections: [] },
  );

  // remove an individual option from a section
  const onDeselect = (
    paramKey: MediaFilterParamKey,
    selected: FilterOption<T>,
    subsectionParamKey?: MediaFilterParamKey,
  ) => {
    const section = sections.get(paramKey);
    const updatedSections = new Map<
      MediaFilterParamKey,
      FilterSection<FilterValue>
    >();

    if (isDefined(section)) {
      const options = section.options.map((option) => ({
        ...option,
        isSelected: option.value === selected.value ? false : option.isSelected,
      }));

      updatedSections.set(section.paramKey, { ...section, options });

      if (isDefined(subsectionParamKey)) {
        onDeselectSection(subsectionParamKey, updatedSections);
      }

      onChange(updatedSections);
    }
  };

  // remove all options from a section
  const onDeselectSection = (
    paramKey: MediaFilterParamKey,
    updatedSections: Map<MediaFilterParamKey, FilterSection<FilterValue>>,
  ) => {
    const section = sections.get(paramKey);

    if (isDefined(section)) {
      const options = section.options.map((option) => ({
        ...option,
        isSelected: false,
      }));

      // update subsection
      updatedSections.set(section.paramKey, { ...section, options });
    }
  };

  const isDistributed = sections
    .get('isDistributed')
    ?.options.find((option) => option.isSelected)?.value;

  const filteredParentSections = filteredSections.filter(
    (section) => !SUBSECTION_PARAM_KEYS.includes(section.paramKey),
  );

  const productIdsSection: FilterSection<string> =
    getSection(sections, 'productIds') ??
    DEFAULT_SELECTED_PRODUCTS_FILTER_SECTION;

  const { data: allProductDetails } = useProductDetailsQuery({
    fetchAllVariants: true,
    productStyleIds: productIdsSection.options
      .filter((option) => option.isSelected)
      .map((option) => option.value),
  });

  const totalCount = filtersCount + extraSectionCount;

  return (
    <>
      {totalCount > 0 && (
        <>
          <CollapsibleSection
            isCollapsed={true}
            rightComponent={<Count>{totalCount}</Count>}
            title='Filtered By'
          >
            <Sections>
              {extraSection && extraSectionCount > 0 && (
                <SelectedOptions
                  onDeselect={(_, option) =>
                    extraSection.onDeselect(option.value)
                  }
                  section={{
                    options: extraSection.options,
                    paramKey: 'manualModerationMediaStatus',
                  }}
                  title={extraSection.title}
                />
              )}
              {filteredParentSections.map((section) => {
                const title =
                  section.paramKey === 'distribution'
                    ? getDistributionTitle(!!isDistributed)
                    : FilterSectionTitle[section.paramKey];

                if (section.paramKey === 'hasLabels') {
                  const subsection = filteredSections.find(
                    ({ paramKey }) => paramKey === 'labels',
                  );

                  return (
                    <SelectedOptions
                      isDisabled={disabledFilters?.includes(section.paramKey)}
                      key={section.paramKey}
                      onDeselect={onDeselect}
                      section={section}
                      subsection={subsection}
                      title={title}
                    />
                  );
                }

                if (section.paramKey === 'hasProducts' && allProductDetails) {
                  const subsection = filteredSections.find(
                    ({ paramKey }) => paramKey === 'productIds',
                  );

                  return (
                    <SelectedOptions
                      isDisabled={disabledFilters?.includes(section.paramKey)}
                      key={section.paramKey}
                      onDeselect={onDeselect}
                      section={section}
                      subsection={
                        subsection && {
                          ...subsection,
                          options: subsection.options.map((option) => ({
                            ...option,
                            label:
                              allProductDetails.productVariants.find(
                                (product) => product.styleId === option.value,
                              )?.title ?? option.label,
                          })),
                        }
                      }
                      title={title}
                    />
                  );
                }

                // Campaign Filters for Retailer client
                if (isRetailer && section.paramKey === 'campaignTypes') {
                  const paramKey = section.paramKey;
                  const subsection = filteredSections.find(
                    ({ paramKey }) => paramKey === 'campaignSubTypes',
                  );
                  return (
                    <SelectedOptions
                      isDisabled={disabledFilters?.includes(section.paramKey)}
                      key={section.paramKey}
                      onDeselect={(_, selected) =>
                        handleCampaignTypeDeselection(
                          paramKey,
                          selected,
                          sections,
                          onChange,
                        )
                      }
                      section={section}
                      subsection={subsection}
                      title={title}
                    />
                  );
                }

                return (
                  <SelectedOptions
                    isDisabled={disabledFilters?.includes(section.paramKey)}
                    key={section.paramKey}
                    onDeselect={onDeselect}
                    section={section}
                    title={title}
                  />
                );
              })}
            </Sections>
            <BodyText>
              <DefaultButton onClick={onReset}>Clear All Filters</DefaultButton>
            </BodyText>
          </CollapsibleSection>
          <HR />
        </>
      )}
    </>
  );
};

const Sections = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[3]};
  `,
);

const Count = styled.span(
  ({ theme }) => `
    display: flex;
    justify-content: center;
    align-items: center;
    width: ${theme.spacings[5]};
    height: ${theme.spacings[5]};
    border-radius: 50%;
    background: ${theme.colors.gray0};
  `,
);
